import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

// Service categories with their sub-services
const serviceCategories = [
  {
    id: 1,
    name: "Production",
    services: [
      { name: "Film Production", slug: "film-production" },
      { name: "Wedding Cinematography", slug: "wedding-cinematography" },
      { name: "Real Estate Media", slug: "real-estate-media" },
      { name: "Event Broadcasting", slug: "event-broadcasting" }
    ]
  },
  {
    id: 2,
    name: "Digital",
    services: [
      { name: "Website Development", slug: "website-development" },
      { name: "SEO Strategies", slug: "seo-strategies" },
      { name: "Digital Advertising", slug: "digital-advertising" },
      { name: "AI Automations", slug: "ai-automations" }
    ]
  },
  {
    id: 3,
    name: "Creative",
    services: [
      { name: "Brand Identity", slug: "brand-identity" },
      { name: "Content Creation", slug: "content-creation" },
      { name: "Graphic Design", slug: "graphic-design" },
      { name: "Motion Graphics", slug: "motion-graphics" }
    ]
  },
  {
    id: 4,
    name: "Consulting",
    services: [
      { name: "Media Strategy", slug: "media-strategy" },
      { name: "Technology Advisory", slug: "technology-advisory" },
      { name: "Budget & Appraisals", slug: "budget-appraisals" },
      { name: "Automation Consulting", slug: "automation-consulting" },
      { name: "Content Strategy", slug: "content-strategy" },
      { name: "Audience Audit", slug: "audience-audit" }
    ]
  }
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    const handleRouteChange = () => {
      if (prevPathRef.current !== location.pathname && isMenuOpen) {
        prevPathRef.current = location.pathname;
        setTimeout(() => {
          setIsMenuOpen(false);
          setServicesOpen(false);
          setExpandedCategory(null);
        }, 0);
      }
    };
    handleRouteChange();
  }, [location.pathname, isMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setIsMenuOpen(!isMenuOpen);
      if (isMenuOpen) {
        setServicesOpen(false);
        setExpandedCategory(null);
      }
      setTimeout(() => setIsAnimating(false), 1000);
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services', hasSubmenu: true },
    { name: 'Stories', path: '/stories' },
    { name: 'Podcasts', path: '/podcasts' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;
  const isServiceActive = () => location.pathname.startsWith('/services');

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled
            ? 'bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link
              to="/"
              className={`font-serif text-2xl md:text-3xl tracking-[0.3em] text-white hover:text-white/70 transition-all duration-500 ${
                isMenuOpen ? 'opacity-0' : 'opacity-100'
              }`}
            >
              KHAN
            </Link>

            {/* Burger Menu Button - Now visible on all screen sizes */}
            <button
              onClick={toggleMenu}
              className="relative w-12 h-12 flex items-center justify-center z-[60] group"
              aria-label="Toggle menu"
            >
              <div className="relative w-7 h-5">
                {/* Top line */}
                <span 
                  className={`absolute left-0 w-full h-[1.5px] bg-white transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] origin-center ${
                    isMenuOpen 
                      ? 'top-1/2 -translate-y-1/2 rotate-45' 
                      : 'top-0 rotate-0'
                  }`}
                />
                {/* Middle line */}
                <span 
                  className={`absolute top-1/2 -translate-y-1/2 left-0 h-[1.5px] bg-white transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] ${
                    isMenuOpen 
                      ? 'w-0 opacity-0' 
                      : 'w-full opacity-100'
                  }`}
                />
                {/* Bottom line */}
                <span 
                  className={`absolute left-0 w-full h-[1.5px] bg-white transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] origin-center ${
                    isMenuOpen 
                      ? 'bottom-1/2 translate-y-1/2 -rotate-45' 
                      : 'bottom-0 rotate-0'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Full Screen Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] ${
          isMenuOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Background Panels - Curtain Effect */}
        <div className="absolute inset-0 flex">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`flex-1 bg-[#0a0a0a] transition-transform duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] origin-top ${
                isMenuOpen ? 'scale-y-100' : 'scale-y-0'
              }`}
              style={{ 
                transitionDelay: isMenuOpen ? `${i * 60}ms` : `${(4 - i) * 40}ms`
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative h-full flex flex-col overflow-y-auto px-6 md:px-12 pt-28 pb-12">
          {/* Top decorative line */}
          <div 
            className={`absolute top-24 left-6 right-6 md:left-12 md:right-12 h-px bg-white/10 transition-all duration-1000 ease-out ${
              isMenuOpen ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
            }`}
            style={{ transitionDelay: isMenuOpen ? '400ms' : '0ms' }}
          />

          {/* Two Column Layout for Desktop */}
          <div className="flex flex-col lg:flex-row lg:gap-20 flex-1">
            {/* Main Navigation Links */}
            <nav className="space-y-2 flex-1 lg:max-w-xl">
              {navLinks.map((link, index) => (
                <div
                  key={link.name}
                  className={`overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] ${
                    isMenuOpen ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{ 
                    transitionDelay: isMenuOpen ? `${300 + index * 80}ms` : `${(7 - index) * 40}ms`
                  }}
                >
                  {link.hasSubmenu ? (
                    // Services with expandable submenu
                    <div>
                      <button
                        onClick={() => setServicesOpen(!servicesOpen)}
                        className={`group flex items-center gap-4 md:gap-6 py-3 md:py-4 w-full transition-all duration-500 ${
                          isMenuOpen ? 'translate-y-0' : 'translate-y-full'
                        }`}
                        style={{ 
                          transitionDelay: isMenuOpen ? `${350 + index * 80}ms` : `${(7 - index) * 40}ms`
                        }}
                      >
                        <span className={`text-xs tracking-wider transition-colors duration-500 ${
                          isServiceActive() ? 'text-white/60' : 'text-white/20 group-hover:text-white/40'
                        }`}>
                          0{index + 1}
                        </span>
                        <span className={`w-6 md:w-8 h-px transition-all duration-500 ${
                          isServiceActive() 
                            ? 'bg-white/60 w-10 md:w-12' 
                            : 'bg-white/20 group-hover:bg-white/40 group-hover:w-10 md:group-hover:w-12'
                        }`} />
                        <span className={`font-serif text-3xl sm:text-4xl md:text-5xl tracking-[0.1em] transition-colors duration-500 ${
                          isServiceActive() ? 'text-white' : 'text-white/40 group-hover:text-white/80'
                        }`}>
                          {link.name}
                        </span>
                        <ChevronDown className={`ml-auto text-white/40 w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 ${
                          servicesOpen ? 'rotate-180' : ''
                        }`} />
                      </button>
                      
                      {/* Services Submenu */}
                      <div className={`overflow-hidden transition-all duration-500 ${
                        servicesOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="pl-12 md:pl-16 pb-4 space-y-2">
                          {/* View All Link */}
                          <Link
                            to="/services"
                            onClick={() => setIsMenuOpen(false)}
                            className="block py-2 text-white/50 text-sm hover:text-white transition-colors"
                          >
                            View All Services →
                          </Link>
                          
                          {/* Categories */}
                          {serviceCategories.map((category, catIdx) => (
                            <div key={category.id} className="border-t border-white/5 pt-3">
                              <button
                                onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                                className="flex items-center justify-between w-full py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-white/30 text-xs">0{catIdx + 1}</span>
                                  <span className="text-white/70 text-sm md:text-base">{category.name}</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-300 ${
                                  expandedCategory === category.id ? 'rotate-180' : ''
                                }`} />
                              </button>
                              
                              {/* Sub-services */}
                              <div className={`overflow-hidden transition-all duration-300 ${
                                expandedCategory === category.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                              }`}>
                                <div className="pl-6 pb-2 space-y-1">
                                  {category.services.map(service => (
                                    <Link
                                      key={service.slug}
                                      to={`/services/${service.slug}`}
                                      onClick={() => setIsMenuOpen(false)}
                                      className={`block py-2 text-sm transition-all ${
                                        location.pathname === `/services/${service.slug}`
                                          ? 'text-white'
                                          : 'text-white/40 hover:text-white/70'
                                      }`}
                                    >
                                      {service.name}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`group flex items-center gap-4 md:gap-6 py-3 md:py-4 transition-all duration-500 ${
                        isMenuOpen ? 'translate-y-0' : 'translate-y-full'
                      }`}
                      style={{ 
                        transitionDelay: isMenuOpen ? `${350 + index * 80}ms` : `${(7 - index) * 40}ms`
                      }}
                    >
                      <span className={`text-xs tracking-wider transition-colors duration-500 ${
                        isActive(link.path) ? 'text-white/60' : 'text-white/20 group-hover:text-white/40'
                      }`}>
                        0{index + 1}
                      </span>
                      <span className={`w-6 md:w-8 h-px transition-all duration-500 ${
                        isActive(link.path) 
                          ? 'bg-white/60 w-10 md:w-12' 
                          : 'bg-white/20 group-hover:bg-white/40 group-hover:w-10 md:group-hover:w-12'
                      }`} />
                      <span className={`font-serif text-3xl sm:text-4xl md:text-5xl tracking-[0.1em] transition-colors duration-500 ${
                        isActive(link.path) ? 'text-white' : 'text-white/40 group-hover:text-white/80'
                      }`}>
                        {link.name}
                      </span>
                      <span className={`ml-auto text-white/20 transition-all duration-500 ${
                        isActive(link.path) 
                          ? 'opacity-100 translate-x-0' 
                          : 'opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0'
                      }`}>
                        →
                      </span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Column - Info (Desktop Only) */}
            <div 
              className={`hidden lg:flex flex-col justify-center transition-all duration-700 ease-out ${
                isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: isMenuOpen ? '600ms' : '0ms' }}
            >
              <div className="space-y-8 max-w-sm">
                <div>
                  <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-3">About</p>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Creative Director specializing in cinematography, digital marketing, and brand development. Serving clients worldwide.
                  </p>
                </div>
                
                <div>
                  <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-3">Location</p>
                  <p className="text-white/60 text-sm">Worldwide</p>
                </div>
                
                <div>
                  <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-3">Connect</p>
                  <Link 
                    to="/contact" 
                    onClick={() => setIsMenuOpen(false)}
                    className="inline-block px-6 py-3 border border-white/20 text-white/70 text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-all duration-500"
                  >
                    Get in Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom section */}
          <div 
            className={`mt-auto transition-all duration-700 ease-out ${
              isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: isMenuOpen ? '700ms' : '0ms' }}
          >
            {/* Decorative line */}
            <div className="h-px bg-white/10 mb-6 md:mb-8" />
            
            <div className="flex justify-between items-end">
              <div className="lg:hidden">
                <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-2">Availability</p>
                <p className="text-white/60 text-sm">Serving Clients Worldwide</p>
              </div>
              
              {/* Animated decorative element */}
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-white/20 text-xs tracking-widest">MENU</span>
                <div className="relative w-8 h-8">
                  <div 
                    className={`absolute inset-0 border border-white/20 transition-transform duration-1000 ease-out ${
                      isMenuOpen ? 'rotate-45 scale-100' : 'rotate-0 scale-0'
                    }`}
                    style={{ transitionDelay: isMenuOpen ? '600ms' : '0ms' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
